import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, openProtectedFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import LiveDeliveryMap from "@/components/LiveDeliveryMap";
import { ArrowLeft, CheckCircle2, Circle, Loader2, Truck, MapPin, FileText } from "lucide-react";

const POLL_MS = 30000;
const LIVE_POLL_MS = 10000;   // while the rider is on the way

const DOC_LABEL = { tax_invoice: "Tax invoice", bill_of_supply: "Bill of supply", credit_note: "Credit note", delivery: "Delivery fee invoice" };

// G10: live order tracking, backed by GET /orders/{id}/tracking. Refreshes
// every 30s while the order is moving, and pauses while the tab is hidden.
export default function OrderTracking() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [otp, setOtp] = useState(null);
  const [error, setError] = useState("");
  const [invoices, setInvoices] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}/tracking`);
      setT(data);
      setError("");
      if (["picked_up", "out_for_delivery", "delivered", "cancelled"].includes(data.status)) {
        api.get(`/orders/${id}/invoices`).then(({ data: inv }) => setInvoices(inv || [])).catch(() => {});
      }
      if (["picked_up", "out_for_delivery"].includes(data.delivery?.status || data.delivery_status) || ["picked_up", "out_for_delivery"].includes(data.status)) {
        api.get(`/customer/orders/${id}/delivery-otp`).then(({ data: o }) => setOtp(o)).catch(() => {});
      } else {
        setOtp(null);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [id]);

  const onTheWay = !!t?.live;
  useEffect(() => {
    load();
    const timer = setInterval(() => { if (!document.hidden) load(); }, onTheWay ? LIVE_POLL_MS : POLL_MS);
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); };
  }, [load, onTheWay]);

  if (error) return <div className="max-w-2xl mx-auto px-6 py-10 text-destructive">{error}</div>;
  if (!t) return <div className="max-w-2xl mx-auto px-6 py-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  const d = t.delivery || {};
  const cancelled = t.status === "cancelled";
  const steps = [
    { key: "placed", label: "Order placed", done: true },
    { key: "accepted", label: "Shop accepted and is packing", done: ["accepted", "preparing", "ready_for_pickup", "picked_up", "out_for_delivery", "delivered"].includes(t.status) || !!d.picked_up_at },
    { key: "ready", label: "Packed — rider on the way to the shop", done: ["ready_for_pickup", "picked_up", "out_for_delivery", "delivered"].includes(t.status) || !!d.picked_up_at },
    { key: "picked", label: "Picked up — on the way to you", done: !!d.picked_up_at || ["picked_up", "out_for_delivery", "delivered"].includes(t.status), at: d.picked_up_at || d.out_for_delivery_at },
    { key: "delivered", label: "Delivered", done: t.status === "delivered", at: d.delivered_at },
  ];

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      <Link to="/account" className="text-sm text-stone-600 hover:underline inline-flex items-center gap-1 mb-4"><ArrowLeft className="h-4 w-4" /> Your orders</Link>
      <h1 className="font-heading text-3xl font-semibold tracking-tight">Order #{t.order_id.slice(0, 8).toUpperCase()}</h1>
      <div className="text-sm text-stone-600 mt-1 flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {t.delivery_address} · {t.delivery_pincode}</div>

      {t.live && !cancelled && (
        <div className="mt-6"><LiveDeliveryMap live={t.live} riderName={d.actor_name} /></div>
      )}

      {cancelled ? (
        <Card className="mt-6 p-5 rounded-2xl border-destructive/30 bg-destructive/5 text-destructive">This order was cancelled.</Card>
      ) : (
        <Card className="mt-6 p-6 rounded-2xl border-stone-200" data-testid="tracking-timeline">
          <ol className="space-y-4">
            {steps.map(s => (
              <li key={s.key} className="flex items-start gap-3">
                {s.done ? <CheckCircle2 className="h-5 w-5 text-teal mt-0.5" /> : <Circle className="h-5 w-5 text-stone-300 mt-0.5" />}
                <div>
                  <div className={s.done ? "font-semibold" : "text-stone-500"}>{s.label}</div>
                  {s.at && <div className="text-xs text-stone-500">{new Date(s.at).toLocaleString()}</div>}
                </div>
              </li>
            ))}
          </ol>
          {d.status === "failed" && (
            <div className="mt-4 text-sm text-destructive">Delivery attempt failed{d.failure_reason ? ` (${d.failure_reason.replace(/_/g, " ")})` : ""}. Choose redelivery or a refund from your orders page.</div>
          )}
        </Card>
      )}

      {(d.actor_name || t.delivery_person_name) && !cancelled && t.status !== "delivered" && (
        <Card className="mt-4 p-4 rounded-2xl border-stone-200 flex items-center gap-3">
          <Truck className="h-5 w-5 text-marigold" />
          <div className="flex-1 text-sm">
            <div><b>{d.actor_name || t.delivery_person_name}</b>{d.partner ? ` from ${d.partner}` : ""} is delivering your order</div>
            {d.partner_track_url && <a href={d.partner_track_url} target="_blank" rel="noreferrer" className="text-xs text-teal hover:underline">Open {d.partner} tracking</a>}
            <div className="text-xs text-stone-500 font-mono">{d.actor_phone || t.delivery_person_phone_masked || t.delivery_person_phone}</div>
          </div>
          {!t.live && t.eta_minutes && <Badge className="bg-marigold/15 text-stoneWarm border-0 rounded-full">~{t.eta_minutes} min</Badge>}
        </Card>
      )}

      {otp?.otp_active && otp.code && (
        <Card className="mt-4 p-4 rounded-2xl border-teal/30 bg-teal/5 text-sm" data-testid="tracking-otp">
          Delivery OTP <span className="font-mono text-2xl font-bold tracking-[0.3em] ml-2">{otp.code}</span>
          <div className="text-xs text-stone-500 mt-1">Share it only with your delivery agent when you receive the order.</div>
        </Card>
      )}
      {invoices.length > 0 && (
        <Card className="mt-4 p-4 rounded-2xl border-stone-200" data-testid="tracking-invoices">
          <div className="text-sm font-semibold mb-2">Invoices</div>
          <div className="space-y-1">
            {invoices.map(i => (
              <button key={i.id} type="button" data-testid={`invoice-${i.id}`}
                onClick={() => openProtectedFile(`/invoices/${i.id}/pdf`).catch(() => toast.error("Couldn't open the invoice — try again"))}
                className="w-full flex items-center gap-2 text-sm text-left rounded-lg px-2 py-1.5 hover:bg-stone-50">
                <FileText className="h-4 w-4 text-teal shrink-0" />
                <span className="flex-1">{DOC_LABEL[i.kind === "delivery" ? "delivery" : i.doc_type] || "Invoice"} · {i.supplier?.trade_name || i.supplier?.legal_name}</span>
                <span className="font-mono text-xs text-stone-500">{i.number}</span>
              </button>
            ))}
          </div>
        </Card>
      )}
      <div className="text-xs text-stone-400 mt-4">Updates automatically every {t.live ? 10 : 30} seconds.</div>
    </div>
  );
}
